import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  fetchOrders,
  updateOrderStatus,
} from "../../../slice/order/orderSlice";
import Pagination from "../component/Pagination";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Logo from "../../../assets/logo.png";

const ITEMS_PER_PAGE = 10;

const ORDER_STATUS = {
  1: { label: "Placed",      class: "ord-placed"      },
  2: { label: "Processing",  class: "ord-processing"  },
  3: { label: "Printing",    class: "ord-printing"    },
  4: { label: "Shipped",     class: "ord-shipped"     },
  5: { label: "Delivered",   class: "ord-delivered"   },
  6: { label: "Hold",        class: "ord-hold"        },
};

// Sidebar order: Received → Printing → Processing → Shipped → Hold → Delivered
const DROPDOWN_ORDER = [1, 3, 2, 4, 6, 5];

/* ── Convert an imported image (webpack URL) into a base64 data URL so jsPDF can embed it ── */
const getImageDataUrl = (url) =>
  new Promise((resolve, reject) => {
    fetch(url)
      .then((res) => res.blob())
      .then((blob) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      })
      .catch(reject);
  });

export default function OrderList({ statusFilter = "placed" }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { loading, error, pagination = {} } = useSelector((state) => state.orders);
  const orders = useSelector((state) => state.orders.orders);

  const [page, setPage] = useState(1);
  const limit = ITEMS_PER_PAGE;
  const [searchTerm, setSearchTerm] = useState("");
  const [printing, setPrinting] = useState(false);

  useEffect(() => { setPage(1); }, [statusFilter]);

  useEffect(() => {
    dispatch(fetchOrders({ page, limit, status: statusFilter }));
  }, [dispatch, page, limit, statusFilter]);

  const handleStatusChange = (order, newStatus) => {
    dispatch(
      updateOrderStatus({ orderId: order._id, orderStatus: newStatus })
    ).then((result) => {
      if (result.meta.requestStatus === "fulfilled") {
        dispatch(fetchOrders({ page, limit, status: statusFilter }));
        window.dispatchEvent(new Event("order-status-changed"));
      }
    });
  };

  const filteredOrders = orders.filter((order) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      order.orderId?.toLowerCase().includes(term) ||
      order.user?.name?.toLowerCase().includes(term) ||
      order.user?.email?.toLowerCase().includes(term) ||
      order.user?.phone?.includes(term)
    );
  });

  /* ── Export PDF ── */
  const handleExportPDF = () => {
    const doc = new jsPDF("l", "mm", "a4");
    const title = statusFilter === "placed" ? "Order Received" : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1);
    const exportDate = new Date().toLocaleDateString("en-IN");

    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(30, 30, 30);
    doc.text("ShaArt — Order Report", 14, 16);

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100);
    doc.text(`Filter: ${title} Orders`,          14, 24);
    doc.text(`Exported on: ${exportDate}`,        14, 30);
    doc.text(`Total Orders: ${filteredOrders.length}`, 14, 36);

    const head = [["SI", "Order ID", "User", "Email", "Items", "Total", "Payment", "Status", "Created"]];

    const body = filteredOrders.map((order, index) => [
      index + 1,
      order.orderId || "-",
      order.user?.name  || order.shippingAddress?.fullName || "-",
      order.user?.email || "-",
      order.items?.length ?? 0,
      `Rs.${order.totalAmount}`,
      order.payment?.method || "-",
      (order.orderStatus === 1 ? "Order Received" : ORDER_STATUS[order.orderStatus]?.label) || order.status || "-",
      new Date(order.createdAt).toLocaleDateString("en-IN"),
    ]);

    autoTable(doc, {
      startY: 42,
      head,
      body,
      theme: "grid",
      styles:             { fontSize: 9, cellPadding: 4, overflow: "linebreak", valign: "middle" },
      headStyles:         { fillColor: [41, 128, 185], textColor: 255, fontStyle: "bold", halign: "center" },
      alternateRowStyles: { fillColor: [245, 249, 255] },
      columnStyles: {
        0: { halign: "center", cellWidth: 16 },
        1: { cellWidth: 32 },
        2: { cellWidth: 30 },
        3: { cellWidth: 52 },
        4: { halign: "center", cellWidth: 14 },
        5: { halign: "right",  cellWidth: 22 },
        6: { halign: "center", cellWidth: 24 },
        7: { halign: "center", cellWidth: 24 },
        8: { halign: "center", cellWidth: 26 },
      },
      didDrawPage: (data) => {
        const totalPages = doc.internal.getNumberOfPages();
        const pageWidth  = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(`Page ${data.pageNumber} of ${totalPages}`, pageWidth / 2, pageHeight - 8, { align: "center" });
        doc.text("ShaArt Admin Export", 14, pageHeight - 8);
      },
    });

    doc.save(`orders-${statusFilter}-${Date.now()}.pdf`);
  };

const handlePrintAddress = async () => {
  if (filteredOrders.length === 0) return;
  setPrinting(true);

  try {
    const logoDataUrl = await getImageDataUrl(Logo);

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: [101.6, 152.4],
    });

    const pageWidth  = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin     = 8;
    const textWidth  = pageWidth - margin * 2;

    const FROM = [
      "SHA Arts,",
      "4/150D, Velliyavilai Jn,",
      "Palappallam Post,",
      "Kanyakumari,",
      "TN-629159",
      "9443558827",
    ];

    const logoW = 45;
    const logoH = 7;
    const logoX = pageWidth - margin - logoW - 10;

    filteredOrders.forEach((order, i) => {
      if (i > 0) doc.addPage([101.6, 152.4], "portrait");

      const addr = order.shippingAddress || order.address || {};

      const TO = [
        ["Order ID",       order.orderId || "-"],
        ["Full Name",      addr.fullName || addr.name || order.user?.name || "-"],
        ["Street Address", addr.street   || addr.addressLine1 || addr.address || "-"],
        ["District",       addr.district || addr.city  || "-"],
        ["State",          addr.state    || "-"],
        ["PIN Code",       addr.pinCode  || addr.postalCode || addr.zip || addr.pincode || "-"],
        ["Phone Number",   addr.phone    || addr.phoneNumber || order.user?.phone || "-"],
      ];

    
      const fromFontSize = 9.5;
      const toFontSize    = 9.5;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(fromFontSize);
      const fromLineCount = FROM.length;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(toFontSize);
      const toWrappedLines = TO.map(([label, value]) =>
        doc.splitTextToSize(`${label}: ${value}`, textWidth)
      );
      const toLineCount = toWrappedLines.reduce((sum, lines) => sum + lines.length, 0);


      const totalLineUnits = fromLineCount + toLineCount + 2 

    
      const topOffset      = margin + 6;
      const gapBetween     = 1.4; 
      const availableSpace = pageHeight - topOffset - margin;

      let lineH = availableSpace / (totalLineUnits + gapBetween);
      lineH = Math.min(lineH, 8);   
      lineH = Math.max(lineH, 4.5); 

  
      const scaledFontSize = lineH < 6 ? 8.5 : toFontSize;
      const scaledFromFontSize = lineH < 6 ? 8.5 : fromFontSize;

      let y = topOffset;

  
      doc.addImage(logoDataUrl, "PNG", logoX, margin - 2, logoW, logoH);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10.5);
      doc.setTextColor(20);
      doc.text("From", margin, y);
      y += lineH;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(scaledFromFontSize);
      doc.setTextColor(40);
      FROM.forEach((line) => {
        doc.text(line, margin, y);
        y += lineH;
      });

      y += lineH * gapBetween;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10.5);
      doc.setTextColor(20);
      doc.text("To", margin, y);
      y += lineH;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(scaledFontSize);
      doc.setTextColor(20);

      TO.forEach(([label, value]) => {
        const fullLine = `${label}: ${value}`;
        const wrapped = doc.splitTextToSize(fullLine, textWidth);
        wrapped.forEach((line) => {
          doc.text(line, margin, y);
          y += lineH;
        });
      });
    });

    doc.save(`addresses-${statusFilter}-${Date.now()}.pdf`);
  } catch (err) {
    console.error("PRINT ADDRESS PDF ERROR:", err);
  } finally {
    setPrinting(false);
  }
};


  /* ── Badge colour helper ── */
  const badgeClass = (status) => {
    switch (status) {
      case 5: return "bg-success";
      case 4: return "bg-primary";
      case 3: return "bg-info";
      case 2: return "bg-warning";
      case 6: return "bg-danger";
      default: return "bg-secondary";
    }
  };

  if (loading) return <p className="text-center mt-4">Loading orders...</p>;
  if (error)   return <p className="text-danger text-center mt-4">{error}</p>;

  return (
    <div className="row">
      <div className="card mt-3">

        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="card-title mb-0 text-capitalize">
            {statusFilter} Orders
          </h4>

          <div className="position-relative">
            <input
              type="text"
              className="form-control form-control-sm ps-4"
              placeholder="Search Order ID, name, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: "220px" }}
            />
            <svg
              width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="#888" strokeWidth="2"
              style={{ position: "absolute", left: "8px", top: "50%", transform: "translateY(-50%)" }}
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </div>

          <div className="d-flex align-items-center gap-2">
            <small className="text-muted">
              {pagination.total ?? 0} total &nbsp;|&nbsp; Showing {orders.length} on this page
            </small>

            {statusFilter === "processing" && (
              <button
                className="btn btn-warning btn-sm"
                onClick={handlePrintAddress}
                disabled={filteredOrders.length === 0 || printing}
              >
                {printing ? "Preparing…" : "🖨️ Print Address"}
              </button>
            )}

            <button
              className="btn btn-info btn-sm"
              onClick={handleExportPDF}
              disabled={filteredOrders.length === 0}
            >
              Export PDF
            </button>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="bg-light">
              <tr>
                <th>Si No</th>
                <th>Order ID</th>
                <th>User</th>
                <th>Items</th>
                <th>Total</th>
                <th>Payment</th>
                <th>Status</th>
                <th>Created</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {filteredOrders.length > 0 ? (
                filteredOrders.map((order, index) => (
                  <tr key={order._id}>
                    <td>{(page - 1) * limit + index + 1}</td>
                    <td>{order.orderId}</td>

                    <td>
                      {order.user?.name || order.shippingAddress?.fullName || "—"}
                      <br />
                      <small className="text-muted">
                        {order.user?.email || order.shippingAddress?.phone || "—"}
                      </small>
                    </td>

                    <td>{order.items?.length}</td>
                    <td>₹{order.totalAmount}</td>

                    <td>
                      <span className="badge bg-info">
                        {order.payment?.method}
                      </span>
                    </td>

                    <td>
                      <span className={`badge ${badgeClass(order.orderStatus)}`}>
                        {order.orderStatus === 1 ? "Order Received" : (ORDER_STATUS[order.orderStatus]?.label || order.status)}
                      </span>
                    </td>

                    <td>{new Date(order.createdAt).toLocaleDateString()}</td>

                    <td>
                      <select
                        className={`ord-status-select ${ORDER_STATUS[order.orderStatus]?.class}`}
                        value={order.orderStatus}
                        onChange={(e) => handleStatusChange(order, Number(e.target.value))}
                      >
                        {DROPDOWN_ORDER.map((key) => (
                          <option key={key} value={key}>
                            {key === 1 ? "Order Received" : ORDER_STATUS[key].label}
                          </option>
                        ))}
                      </select>

                      &nbsp;

                      <button
                        className="btn btn-soft-primary btn-sm"
                        onClick={() => navigate(`/admin/order/view/${order._id}`)}
                      >
                        👁
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="text-center text-muted py-4">
                    No {statusFilter} orders found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="card-footer border-top">
          <Pagination
            page={page}
            totalPages={pagination.totalPages}
            onPageChange={setPage}
          />
        </div>

      </div>
    </div>
  );
}